import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Checkbox,
} from '@mui/material';
import { useState } from 'react';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  HeadTableCell,
  RowTableCell,
  GeneralTableBox,
} from '../../../common';
import { formatCurrency } from '../utils/externalPaymentUtils';

const XnetAnalysisDialog = ({ open, analysisData, onClose, onGenerateXnetFile }) => {
  const [selectedCoupons, setSelectedCoupons] = useState([]);

  if (!analysisData) return null;

  const { txt_info = [], founded_coupons = [], summary = {} } = analysisData;

  const handleCheckboxChange = (index, isChecked) => {
    const txtItem = txt_info[index];
    const dbCoupon = founded_coupons[index];

    if (isChecked) {
      setSelectedCoupons((prev) => [
        ...prev,
        {
          uuid: dbCoupon.uuid,
          paymentDate: txtItem.fecha_pago_formatted,
          contractNumber: dbCoupon.contract_number,
          month: dbCoupon.month,
          year: dbCoupon.year,
          amount: txtItem.importe,
          couponId: txtItem.cupon_id,
        },
      ]);
    } else {
      setSelectedCoupons((prev) => prev.filter((coupon) => coupon.uuid !== dbCoupon.uuid));
    }
  };

  const handleGenerateFile = () => {
    if (selectedCoupons.length > 0 && onGenerateXnetFile) {
      onGenerateXnetFile(selectedCoupons);
    }
  };

  const isSelected = (dbCouponUuid) => {
    return selectedCoupons.some((coupon) => coupon.uuid === dbCouponUuid);
  };

  return (
    <DialogContainer open={open} onClose={onClose} size="large">
      <DialogTitle>Análisis de Archivo XNET</DialogTitle>
      <DialogBody>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Resumen del Análisis
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Cupones en archivo: ${summary.cantidad_cupones_archivo || 0}`}
              color="info"
            />
            <Chip
              label={`Cupones procesados: ${summary.cantidad_cupones_procesados || 0}`}
              color="primary"
            />
            <Chip
              label={`Cupones encontrados: ${summary.cantidad_cupones_encontrados || 0}`}
              color="success"
            />
            <Chip
              label={`Importe total: ${formatCurrency(summary.importe_total_archivo || 0)}`}
              color="secondary"
            />
          </Box>
        </Box>

        <GeneralTableBox>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <HeadTableCell colSpan={4} align="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Información del Archivo
                  </Typography>
                </HeadTableCell>
                <HeadTableCell/>
                <HeadTableCell colSpan={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Información de la Base de Datos
                  </Typography>
                </HeadTableCell>
                <HeadTableCell/>
                <HeadTableCell>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Seleccionar
                  </Typography>
                </HeadTableCell>
              </TableRow>
              <TableRow>
                <HeadTableCell>Contrato</HeadTableCell>
                <HeadTableCell>Importe</HeadTableCell>
                <HeadTableCell>Fecha Pago</HeadTableCell>
                <HeadTableCell>Código</HeadTableCell>
                <HeadTableCell></HeadTableCell>

                <HeadTableCell>Estado</HeadTableCell>
                <HeadTableCell>Cupón BD</HeadTableCell>
                <HeadTableCell>Mes/Año</HeadTableCell>
                <HeadTableCell>Importes BD</HeadTableCell>
                <HeadTableCell></HeadTableCell>

                <HeadTableCell>Procesar</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txt_info.map((txtItem, index) => {
                const dbCoupon = founded_coupons[index];
                const hasError = dbCoupon && dbCoupon.error;
                const isUnpaid = !hasError && !dbCoupon?.is_paid;

                return (
                  <TableRow key={index}>
                    <RowTableCell>{txtItem.numero_orden}</RowTableCell>
                    <RowTableCell>{formatCurrency(txtItem.importe)}</RowTableCell>
                    <RowTableCell>
                      {txtItem.fecha_pago_formatted}
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        ({txtItem.fecha_pago.dia}/{txtItem.fecha_pago.mes}/{txtItem.fecha_pago.anio}
                        )
                      </Typography>
                    </RowTableCell>
                    <RowTableCell>{txtItem.cupon_id}</RowTableCell>
                    <RowTableCell/>

                    <RowTableCell>
                      {hasError ? (
                        <Chip label="Error" color="error" size="small" />
                      ) : (
                        <Chip
                          label={dbCoupon?.is_paid ? 'Pagado' : 'Pendiente'}
                          color={dbCoupon?.is_paid ? 'warning' : 'success'}
                          size="small"
                        />
                      )}
                    </RowTableCell>

                    <RowTableCell>
                      {hasError ? (
                        <Typography color="error" variant="body2">
                          {dbCoupon.error}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2">
                            Contrato: {dbCoupon?.contract_number}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            UUID: {dbCoupon?.uuid?.slice(0, 8)}...
                          </Typography>
                        </>
                      )}
                    </RowTableCell>

                    <RowTableCell>
                      {hasError ? (
                        <Typography color="error" variant="body2">
                          {dbCoupon.searched_month
                            ? `${dbCoupon.searched_month}/${dbCoupon.searched_year}`
                            : 'N/A'}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {dbCoupon?.month}/{dbCoupon?.year}
                        </Typography>
                      )}
                    </RowTableCell>

                    <RowTableCell>
                      {hasError ? (
                        <Typography color="error" variant="body2">
                          -
                        </Typography>
                      ) : (
                        <Box>
                          <Typography variant="caption" display="block">
                            1°: {formatCurrency(dbCoupon?.first_due_amount || 0)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            2°: {formatCurrency(dbCoupon?.second_due_amount || 0)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            3°: {formatCurrency(dbCoupon?.third_due_amount || 0)}
                          </Typography>
                        </Box>
                      )}
                    </RowTableCell>
                    <RowTableCell/>

                    <RowTableCell align="center">
                      {isUnpaid ? (
                        <Checkbox
                          checked={isSelected(dbCoupon.uuid)}
                          onChange={(event) => handleCheckboxChange(index, event.target.checked)}
                          color="primary"
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {hasError ? 'Error' : 'Ya pagado'}
                        </Typography>
                      )}
                    </RowTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </GeneralTableBox>

        {selectedCoupons.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>{selectedCoupons.length}</strong> cupón
              {selectedCoupons.length > 1 ? 'es' : ''} seleccionado
              {selectedCoupons.length > 1 ? 's' : ''} para procesar
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Importe total:{' '}
              {formatCurrency(selectedCoupons.reduce((sum, coupon) => sum + coupon.amount, 0))}
            </Typography>
          </Box>
        )}
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={handleGenerateFile}
      />
    </DialogContainer>
  );
};

export default XnetAnalysisDialog;
